import { Flex, FlexProps, Icon, Text } from '@chakra-ui/react'
import React, { Key, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BetaBadge } from '~/components/shared/BetaBadge'
import { NewBadge } from '~/components/shared/NewBadge'
import { NotificationBadge } from '~/components/shared/NotificationBadge'
import { Tooltip } from '~/components/ui/tooltip'
import {
  SiteNavigationTabSizes,
  useSiteNavigationTabStyle,
} from '~/pages/Home/SiteNavigation/componentStyles/SiteNavigationTab.style'
import { Size } from '~/types/ThemeTypes'

const MAX_NOTIFICATION_COUNT = 999
const getNotificationCountString = (count?: number) => {
  if (count === undefined || count <= 0) return undefined

  return count > MAX_NOTIFICATION_COUNT ? `${MAX_NOTIFICATION_COUNT}+` : count
}

interface SiteNavigationTabProps extends Omit<FlexProps, 'id' | 'onClick'> {
  id?: Key
  title: string
  isCollapsed?: boolean
  icon?: React.ComponentType<any>
  isSelected?: boolean
  isBeta?: boolean
  isNew?: boolean
  notificationCount?: number
  postfixContent?: React.ReactNode
  onClick?: (id?: Key) => void
}

export const SiteNavigationTab = (props: SiteNavigationTabProps) => {
  const {
    id,
    title,
    isCollapsed,
    icon,
    isSelected,
    isBeta,
    isNew,
    notificationCount,
    postfixContent,
    onClick,
    ...rest
  } = props
  const { t } = useTranslation()

  const getTabSize = () => {
    if (isCollapsed && (isNew || isBeta)) {
      return SiteNavigationTabSizes.CollapsedBadged
    }
    if (isCollapsed) {
      return SiteNavigationTabSizes.Collapsed
    }

    return SiteNavigationTabSizes.Normal
  }

  const styles = useSiteNavigationTabStyle({
    size: getTabSize(),
  })

  const handleClick = () => {
    onClick?.(id)
  }

  const notificationCountString = getNotificationCountString(notificationCount)
  const notificationLabel = notificationCountString
    ? t('a11y.nav.notificationCount', { count: notificationCountString })
    : undefined
  const notificationAriaLabel = notificationLabel
    ? `${title}, ${notificationLabel}`
    : undefined
  const selectedProps = {
    'aria-current': isSelected ? ('page' as const) : undefined,
    'data-selected': isSelected ? '' : undefined,
  }
  const iconSelectedProps = { 'data-selected': selectedProps['data-selected'] }

  const tabContent = useMemo(() => {
    if (!isCollapsed) {
      return (
        <Flex key={id} css={styles.container}>
          <Flex asChild css={styles.selectionContainer} {...rest}>
            <button
              type="button"
              aria-label={notificationAriaLabel}
              {...selectedProps}
              onClick={handleClick}
            >
              <Flex css={styles.contentWrapper}>
                <Flex css={styles.iconWrapper}>
                  {icon && (
                    <Icon {...iconSelectedProps} css={styles.icon}>
                      {React.createElement(icon, { size: '100%' })}
                    </Icon>
                  )}
                  <Text
                    css={styles.titleText}
                    lineClamp={1}
                    {...iconSelectedProps}
                  >
                    {title}
                  </Text>
                </Flex>

                {notificationCountString && (
                  <NotificationBadge
                    aria-hidden
                    count={notificationCountString}
                    css={styles.notificationBadge}
                    size={Size.md}
                  />
                )}
                {isBeta && <BetaBadge css={styles.statusBadge} />}
                {isNew && <NewBadge css={styles.statusBadge} />}
                {postfixContent}
              </Flex>
            </button>
          </Flex>
        </Flex>
      )
    }

    return (
      <Flex key={id} css={styles.container}>
        <Tooltip showArrow content={title} positioning={{ placement: 'right' }}>
          <Flex asChild css={styles.selectionContainer}>
            <button
              type="button"
              aria-label={notificationAriaLabel ?? title}
              {...selectedProps}
              onClick={handleClick}
            >
              {icon && (
                <Icon {...iconSelectedProps} css={styles.icon}>
                  {React.createElement(icon, { size: '100%' })}
                </Icon>
              )}
              {notificationCountString && (
                <NotificationBadge
                  count={notificationCountString}
                  css={styles.notificationBadge}
                  size="dot"
                />
              )}
              {isBeta && <BetaBadge css={styles.statusBadge} />}
              {isNew && <NewBadge css={styles.statusBadge} />}
              {postfixContent}
            </button>
          </Flex>
        </Tooltip>
      </Flex>
    )
  }, [
    title,
    isCollapsed,
    icon,
    isSelected,
    isBeta,
    isNew,
    notificationCount,
    notificationAriaLabel,
    postfixContent,
  ])

  return <>{tabContent}</>
}
